/* istanbul ignore file */

import type { UseValidationEngineOptions, ValidationEngine } from './ValidationEngine.interface';

import type {
    Conditions,
    DispatchMode,
    MergeStrategy,
    ValidationConfig,
    ValidationEngineRule,
    ValidationContext,
    ValidationResult,
    ValidationRuleType
} from './types';
import { isFieldNode, isGroupNode, isCollectionNode } from './guards';
import { validateValue } from './validateValue';
import { doesRuleApplyToFlow } from './flow';
import { expandPathAgainstValues, stripIndices } from './utils/paths';
import { getValueAtPath, setValueAtPath } from './utils/valuePath';
import { deepClonePlain, deepMergePlain } from './utils/plainObjects';
import { computeErrorPrefix } from './utils/errorPrefix';
import { deepHasError, hasLeafAtPath, setNestedDefault, setNestedError } from './utils/errorState';
import { mergeConditions } from './utils/conditions';
import type { ScopeBinding } from './utils/scopeBinding';
import { getScopeBinding } from './utils/scopeBinding';
import { getConditionScopeForPath } from './utils/conditionScope';

export type { DispatchMode, MergeStrategy } from './types';
export type { UseValidationEngineOptions, ValidationEngine } from './ValidationEngine.interface';

/**
 * Creates and returns a new validation engine instance.
 */
export function createValidationEngine(options?: UseValidationEngineOptions): ValidationEngine {
    const dispatchMode: DispatchMode = options?.dispatchMode ?? 'onCommit';
    const optionalPaths = new Set<string>();
    const ruleStore = new Map<string, ValidationEngineRule[]>();
    const errorStore = new Map<string, ValidationResult>();
    // Track entity/group and collection prefixes for dynamic GC messages
    const groupPrefixMap = new Map<string, string>();
    const collectionPrefixMap = new Map<string, string>();

    // Precomputed binding from ruleKey -> scope root.
    // - For collections: scope is the indexed collection element (e.g. other.tradeIns[0])
    // - For non-collection groups: scope is the outer-most group root for that branch (e.g. other.tradeIn)
    const scopeBindingByRuleKey = new Map<string, ScopeBinding>();

    const flowLocked = Boolean(options?.lockFlow);

    const dispatchOnError = (entries: Array<[string, ValidationResult]>) => {
        if (dispatchMode !== 'onError') return;

        const commitConfig = options?.commitConfig;
        if (!commitConfig || typeof commitConfig.onCommit !== 'function') return;

        if (!entries.length) return;

        const keyStrategy = commitConfig.keyStrategy ?? 'leaf';
        const errorFlags: Record<string, boolean> = {};

        for (const [path, result] of entries) {
            const normalized = path.replace(/\[(\d+)\]/g, '.$1');
            const derivedKey =
                keyStrategy === 'path' ? normalized : normalized.split('.').filter(Boolean).slice(-1)[0] ?? normalized;

            const key = commitConfig.keyMap?.[path] ?? derivedKey;
            errorFlags[key] = !result.valid;
        }

        commitConfig.onCommit(errorFlags);
    };

    const ctx: ValidationContext = {
        values: options?.initialValues ? deepClonePlain(options.initialValues) : {},
        path: '',
        flow: options?.flow ?? 'default',
        uiMode: dispatchMode === 'onCommit' ? 'compact-ui' : 'deal-screen',
        optionalPaths
    };

    /**
     * Clears cached validation results for a path prefix.
     *
     * Removes:
     * - the exact prefix key
     * - any descendant key where the prefix is followed by a dot (nested object) or an index (collection)
     *
     * Used by mutators (e.g., `setValue`) to prevent returning stale cached results.
     */
    const clearErrorsForPrefix = (prefix: string) => {
        if (!prefix) return;
        const startsWithDot = `${prefix}.`;
        const startsWithIndex = `${prefix}[`;
        for (const key of Array.from(errorStore.keys())) {
            if (key === prefix || key.startsWith(startsWithDot) || key.startsWith(startsWithIndex)) {
                errorStore.delete(key);
            }
        }
    };

    /**
     * Expands base rule paths into concrete evaluation paths using the current values tree.
     *
     * Example: `phones.number` -> [`phones[0].number`, `phones[1].number`]
     *
     * If nothing expands (e.g., the collection doesn't exist yet), it falls back to the base path so callers can
     * still seed defaults / compute a stable shape.
     */
    const expandEvaluationPaths = (basePaths: string[]): string[] => {
        const out: string[] = [];
        for (const basePath of basePaths) {
            const expanded = expandPathAgainstValues(basePath, ctx.values);
            if (expanded.length) {
                out.push(...expanded);
            } else {
                // If nothing expanded (e.g. missing values), fall back to the base path.
                out.push(basePath);
            }
        }
        return out;
    };

    /**
     * Converts user-supplied include paths into the set of leaf validation paths that should be evaluated.
     *
     * Supports:
     * - Explicit leaf paths (when the stripped rule key exists in `ruleStore`)
     * - Subtree roots (expanded to all descendant leaf rule keys)
     * - Indexed collection paths (preserves the caller's index while expanding descendants)
     *
     * The final set is expanded against current values to produce concrete indexed evaluation paths.
     */
    const expandIncludePathsToLeafPaths = (includePaths: string[]): string[] => {
        if (!includePaths.length) return [];

        const ruleKeys = Array.from(ruleStore.keys());
        const out: string[] = [];

        for (const requestedPath of includePaths) {
            const requestedRuleKey = stripIndices(requestedPath);
            const hasIndex = /\[\d+\]/.test(requestedPath);

            // If this resolves to a registered leaf rule, treat it as an explicit leaf include.
            if (ruleStore.has(requestedRuleKey)) {
                out.push(requestedPath);
                continue;
            }

            // Otherwise, treat it as a subtree root and expand to all descendant leaf rules.
            const descendants = ruleKeys.filter((k) => k === requestedRuleKey || k.startsWith(`${requestedRuleKey}.`));

            if (!descendants.length) {
                out.push(requestedPath);
                continue;
            }

            for (const descendantKey of descendants) {
                if (descendantKey === requestedRuleKey) {
                    out.push(requestedPath);
                    continue;
                }

                const suffix = descendantKey.slice(requestedRuleKey.length); // includes leading '.'
                out.push(hasIndex ? `${requestedPath}${suffix}` : descendantKey);
            }
        }

        // Expand against ctx.values so callers can pass non-indexed collection paths like `phones.number`
        // and receive indexed evaluation paths like `phones[0].number`.
        return Array.from(new Set(expandEvaluationPaths(out)));
    };

    /**
     * Evaluates a path under a specific flow without mutating the engine's current flow or warming the main cache.
     *
     * This exists so callers can ask "what would the errors look like for flow X" while keeping the engine's
     * internal `ctx.flow` and `errorStore` intact.
     */
    const evaluatePathForFlow = (path: string, flow: ValidationContext['flow'], value?: any): ValidationResult => {
        const ruleKey = path.replace(/\[\d+\]/g, '');
        const rules = ruleStore.get(ruleKey) || [];

        // Mirror parent-existence short-circuit behavior from evaluatePath(), but do not persist in errorStore.
        const lastDot = path.lastIndexOf('.');
        if (lastDot > 0) {
            const parentPath = path.slice(0, lastDot);
            const parentValue = getValueAtPath(ctx.values, parentPath);
            if (parentValue === undefined || parentValue === null) {
                return { valid: true, channels: {} };
            }
        }

        const v = value ?? getValueAtPath(ctx.values, path);
        const optional = ctx.optionalPaths?.has(path) ?? false;

        const effectivePrefix = computeErrorPrefix(path, groupPrefixMap, collectionPrefixMap);
        const scopedCtx: ValidationContext = {
            ...ctx,
            path,
            flow,
            errorPrefix: effectivePrefix
        };

        const scope = getConditionScopeForPath(path, ctx.values, scopeBindingByRuleKey);

        return validateValue(rules, v, {
            optional,
            ctx: scopedCtx as ValidationContext,
            messageResolver: options?.messageResolver,
            scope
        });
    };

    /**
     * Evaluates a single path using the current engine context and persists the result in `errorStore`.
     *
     * Notes:
     * - Skips validating leaf rules when the parent object is missing (avoids emitting errors for non-existent branches)
     * - Updates `ctx.path` and `ctx.errorPrefix` so custom evaluators can derive dynamic behavior
     */
    const evaluatePath = (path: string, value?: any): ValidationResult => {
        const ruleKey = path.replace(/\[\d+\]/g, '');
        const rules = ruleStore.get(ruleKey) || [];

        // If the parent object doesn't exist in the values tree, skip evaluation.
        // Example: values.buyer is undefined -> skip validating buyer.firstName/lastName/etc.
        const lastDot = path.lastIndexOf('.');
        if (lastDot > 0) {
            const parentPath = path.slice(0, lastDot);
            const parentValue = getValueAtPath(ctx.values, parentPath);
            if (parentValue === undefined || parentValue === null) {
                const res: ValidationResult = { valid: true, channels: {} };
                errorStore.set(path, res);
                return res;
            }
        }

        const v = value ?? getValueAtPath(ctx.values, path);
        const optional = ctx.optionalPaths?.has(path) ?? false;

        // Update the current path in context so CUSTOM evaluators can derive entity-specific logic
        ctx.path = path;
        // Compute dynamic prefix from group and collection context
        const effectivePrefix = computeErrorPrefix(path, groupPrefixMap, collectionPrefixMap);
        ctx.errorPrefix = effectivePrefix;

        const scope = getConditionScopeForPath(path, ctx.values, scopeBindingByRuleKey);

        const res = validateValue(rules, v, { optional, ctx, messageResolver: options?.messageResolver, scope });
        errorStore.set(path, res);
        return res;
    };

    const engine: ValidationEngine = {
        ctx,
        registerRules: (path: string, rules: ValidationEngineRule[], opts?: { replace?: boolean }) => {
            const existing = ruleStore.get(path) || [];
            const next = opts?.replace ? rules.slice() : existing.concat(rules);
            ruleStore.set(path, next);
            if (opts?.replace) {
                // Clearing cached errors for this path on replace to avoid stale states
                errorStore.delete(path);
            }
        },
        registerConfig: (config: ValidationConfig, opts?: { mergeStrategy?: MergeStrategy }) => {
            const mergeStrategy: MergeStrategy = opts?.mergeStrategy ?? 'append';
            const shouldReplace = mergeStrategy === 'replace';

            const registerLeafRules = (
                path: string,
                rules: ValidationEngineRule[],
                scopeRootPath?: string,
                collectionRootPath?: string,
                inheritedConditions?: Conditions
            ) => {
                // Wrapper conditions (from ancestor $group/$collection nodes) are merged into each leaf rule so
                // `validatePath()` can remain a simple leaf lookup in `ruleStore`.
                const nextRules = inheritedConditions
                    ? rules.map((r) => {
                          const merged = mergeConditions(inheritedConditions, r.conditions);
                          return merged ? { ...r, conditions: merged } : r;
                      })
                    : rules;
                engine.registerRules(path, nextRules, { replace: shouldReplace });

                const binding = getScopeBinding(scopeRootPath, collectionRootPath);
                if (binding) scopeBindingByRuleKey.set(path, binding);
            };

            // NOTE: This engine currently compiles ValidationConfig into a flattened `ruleStore` keyed by leaf paths.
            // `validatePath()` then validates *only* the requested leaf path by fetching its rules from `ruleStore`.
            // Because wrapper nodes ($group/$collection) are not retained for runtime traversal, any wrapper-level
            // conditions must be merged into descendant leaf rules at registration-time to have any effect.
            //
            // TODO(group-level gating): Consider switching to true subtree gating (evaluate wrapper conditions once
            // per group / per collection element and skip descending entirely). That would reduce repeated `when`
            // evaluations, but requires either (a) retaining a compiled tree for runtime traversal, or (b) building
            // a separate precomputed "gate" structure that `validatePath()` can consult before validating a leaf.

            const walk = (
                node: ValidationConfig | ValidationEngineRule[] | unknown,
                basePath: string,
                scopeRootPath?: string,
                collectionRootPath?: string,
                inheritedConditions?: Conditions
            ) => {
                if (!node) return;
                // Field rules array
                if (Array.isArray(node)) {
                    registerLeafRules(
                        basePath,
                        node as ValidationEngineRule[],
                        scopeRootPath,
                        collectionRootPath,
                        inheritedConditions
                    );
                    return;
                }
                // ValidationConfig mapping
                const entries = Object.entries(node as ValidationConfig);
                for (const [key, child] of entries) {
                    const nextPath = basePath ? `${basePath}.${key}` : key;
                    if (isFieldNode(child as any)) {
                        const field = child as any;
                        const rawRules = field.rules as ValidationEngineRule[];
                        registerLeafRules(nextPath, rawRules, scopeRootPath, collectionRootPath, inheritedConditions);
                    } else if (isGroupNode(child as any)) {
                        const grp: any = child as any;
                        if (grp.errorPrefix) groupPrefixMap.set(nextPath, grp.errorPrefix);
                        const isNewScopeRoot = !scopeRootPath && !collectionRootPath;
                        const nextScopeRoot = isNewScopeRoot ? nextPath : scopeRootPath;
                        const nextInherited = mergeConditions(inheritedConditions, grp.conditions);
                        walk(grp.$group, nextPath, nextScopeRoot, collectionRootPath, nextInherited);
                    } else if (isCollectionNode(child as any)) {
                        const coll: any = child as any;
                        if (coll.errorPrefix) collectionPrefixMap.set(nextPath, coll.errorPrefix);
                        const nextInherited = mergeConditions(inheritedConditions, coll.conditions);
                        walk(coll.$collection, nextPath, scopeRootPath, nextPath, nextInherited);
                    } else if (child && typeof child === 'object') {
                        walk(child as ValidationConfig, nextPath, scopeRootPath, collectionRootPath, inheritedConditions);
                    }
                }
            };
            walk(config, '', undefined, undefined, undefined);
        },
        setValue: (
            path: string,
            value: unknown,
            options?: { createMissing?: boolean; validate?: boolean; clearErrors?: boolean }
        ) => {
            const createMissing = options?.createMissing ?? true;
            // TODO(setValue-default-validate): Consider whether `validate` should default to true.
            // Pros: reduces call-site noise for “set-and-validate on change” patterns.
            // Cons: can change UX timing (errors on every change), add extra work on hot paths, and
            // forces callers who only want to sync values (or rely on external validation) to
            // opt out with `{ validate: false }`.
            // Preferred approach (if we want nicer ergonomics): provide a helper/wrapper like
            // `setAndValidate(path, value)` at the hook/component layer rather than changing the
            // core engine default.
            const validate = options?.validate ?? false;
            const clearErrors = options?.clearErrors ?? true;

            if (createMissing) {
                setValueAtPath(ctx.values, path, value);
            } else {
                const didSet = setValueAtPath(ctx.values, path, value, { createMissing: false });
                if (!didSet) return undefined;
            }

            if (clearErrors) {
                clearErrorsForPrefix(path);
            }

            if (validate) {
                // TODO(setValue-subtree-validate): Consider supporting a subtree validation option for object-path writes
                // (e.g. setting `billingAddress`). Today this validates only the exact `path`, but most rules are
                // registered at leaf paths like `billingAddress.zip`, so callers may need `validateAll()` or a future
                // `validateSubtree()` helper.
                return engine.validatePath(path);
            }

            return undefined;
        },
        setValues: (
            values: ValidationContext['values'],
            options?: { mode?: 'replace' | 'merge'; clone?: boolean; validate?: boolean }
        ) => {
            const mode = options?.mode ?? 'replace';
            const clone = options?.clone ?? true;
            const validate = options?.validate ?? false;

            const next = clone ? deepClonePlain(values) : values;

            if (mode === 'merge') {
                ctx.values = deepMergePlain(ctx.values, next);
            } else {
                ctx.values = next;
            }

            // Values changed -> cached errors may be stale.
            errorStore.clear();

            if (validate) {
                engine.validateAll();
            }
        },
        setOptional: (path: string, optional: boolean) => {
            if (optional) optionalPaths.add(path);
            else optionalPaths.delete(path);
        },
        setFlow: (flow) => {
            if (flowLocked) return;
            if (engine.ctx.flow !== flow) {
                engine.ctx.flow = flow;
                // Clear cached results so subsequent reads don't return stale errors
                errorStore.clear();
            }
        },
        setUiMode: (mode) => {
            engine.ctx.uiMode = mode;
        },
        setError: (path: string, isValid: boolean, message?: string | { inline?: string; gcalert?: string }) => {
            const channels: ValidationResult['channels'] = {};
            if (!isValid) {
                const inlineMsg = typeof message === 'string' ? message : message?.inline;
                const gcMsg = typeof message === 'string' ? message : message?.gcalert ?? inlineMsg;
                if (inlineMsg) channels.inline = [inlineMsg];
                if (gcMsg) channels.gcalert = [gcMsg];
            }
            const res: ValidationResult = { valid: isValid, channels };
            errorStore.set(path, res);
        },
        validatePath: (path: string, value?: any): ValidationResult => {
            const prev = errorStore.get(path);
            const res = evaluatePath(path, value);

            // In onError mode, only dispatch when the validation state changes.
            // - valid -> invalid
            // - invalid -> valid
            // - prev unknown (treat as valid) -> invalid
            const shouldDispatch = prev ? prev.valid !== res.valid : !res.valid;
            if (shouldDispatch) {
                dispatchOnError([[path, res]]);
            }
            return res;
        },
        validateAll: () => {
            const out: Record<string, ValidationResult> = {};
            const dispatchEntries: Array<[string, ValidationResult]> = [];

            const evalPaths = expandEvaluationPaths(Array.from(ruleStore.keys()));
            for (const path of evalPaths) {
                const prev = errorStore.get(path);
                const res = evaluatePath(path);
                out[path] = res;
                const shouldDispatch = prev ? prev.valid !== res.valid : !res.valid;
                if (shouldDispatch) dispatchEntries.push([path, res]);
            }

            dispatchOnError(dispatchEntries);
            return out;
        },
        getErrors: (path: string) => {
            return errorStore.get(path);
        },
        getAllErrors: () => {
            const out: Record<string, ValidationResult> = {};
            errorStore.forEach((val, key) => {
                out[key] = val;
            });
            return out;
        },
        clearErrors: (path?: string) => {
            // TODO(fluent-api): Consider returning the engine instance (e.g., `return engine`) to support chaining like
            // `engine.clearErrors().commit()`. If adopted, apply consistently across mutating methods.
            if (path) {
                errorStore.delete(path);
            } else {
                errorStore.clear();
            }
        },
        getFormFields: (options?: {
            flow?: ValidationContext['flow'];
            validationType?: ValidationRuleType;
            match?: 'all' | 'any';
            keyStrategy?: 'leaf' | 'path';
        }) => {
            const keyStrategy = options?.keyStrategy ?? 'leaf';
            const match = options?.match ?? 'all';
            const out = new Set<string>();

            const predicates: Array<(rule: ValidationEngineRule) => boolean> = [];

            if (options?.flow) {
                const activeFlow = options.flow ?? engine.ctx.flow;
                predicates.push((rule) => doesRuleApplyToFlow(rule.flow, activeFlow));
            }

            if (options?.validationType) {
                predicates.push((rule) => rule.type === options.validationType);
            }

            ruleStore.forEach((rules, path) => {
                const hasApplicableRule = (rules ?? []).some((rule) => {
                    if (!predicates.length) return true;
                    return match === 'any' ? predicates.some((p) => p(rule)) : predicates.every((p) => p(rule));
                });

                if (!hasApplicableRule) return;

                const normalized = path.replace(/\[(\d+)\]/g, '.$1');
                const derivedKey =
                    keyStrategy === 'path' ? normalized : normalized.split('.').filter(Boolean).slice(-1)[0] ?? normalized;

                out.add(derivedKey);
            });

            return Array.from(out).sort();
        },
        // TODO
        // - Return errors by flow
        // - Have a $hasError root flag for quick checks in components, so they don't have to check channels or messages
        getFormErrorState: (options?: {
            channel?: keyof ValidationResult['channels'];
            refresh?: boolean;
            includePaths?: string[];
            onlyActive?: boolean;
            flow?: ValidationContext['flow'];
        }) => {
            const channel = options?.channel ?? 'inline';
            const flow = options?.flow;
            const refresh = options?.refresh ?? true;
            const onlyActive = options?.onlyActive ?? false;

            const expandedIncludePaths = options?.includePaths?.length ? expandIncludePathsToLeafPaths(options.includePaths) : [];
            const hasIncludePaths = expandedIncludePaths.length > 0;

            // Optionally refresh validations based on current ctx.values and registered rules
            // NOTE: When a flow override is provided, we compute flow-scoped results without mutating the engine's flow
            // or overwriting the main errorStore.
            const scopedEntries: Array<[string, ValidationResult]> = [];
            if (flow) {
                const evalPaths = hasIncludePaths ? expandedIncludePaths : expandEvaluationPaths(Array.from(ruleStore.keys()));
                for (const p of evalPaths) {
                    scopedEntries.push([p, evaluatePathForFlow(p, flow)]);
                }
            } else if (refresh) {
                if (hasIncludePaths) {
                    for (const p of expandedIncludePaths) engine.validatePath(p);
                } else {
                    engine.validateAll();
                }
            }

            const formErrors: any = {};

            // 1) Seed defaults for known rule paths unless onlyActive requested.
            // If includePaths is provided, only seed defaults for those paths.
            if (!onlyActive) {
                const seedPaths = flow
                    ? scopedEntries.map(([path]) => path)
                    : hasIncludePaths
                    ? expandedIncludePaths
                    : Array.from(ruleStore.keys());

                for (const path of seedPaths) {
                    if (!hasLeafAtPath(formErrors, path)) setNestedDefault(formErrors, path);
                }
            }

            // 2) Overlay actual results onto defaults (or build active-only structure)
            if (flow) {
                for (const [path, result] of scopedEntries) {
                    if (!onlyActive || !result.valid) setNestedError(formErrors, path, result, channel);
                }
            } else if (hasIncludePaths) {
                for (const path of expandedIncludePaths) {
                    const result = errorStore.get(path) ?? engine.validatePath(path);
                    if (!result) continue;
                    if (!onlyActive || !result.valid) setNestedError(formErrors, path, result, channel);
                }
            } else {
                errorStore.forEach((result, path) => {
                    if (!onlyActive || !result.valid) setNestedError(formErrors, path, result, channel);
                });
            }
            return formErrors;
        },
        getHasFormErrors: (options?: {
            channel?: keyof ValidationResult['channels'];
            refresh?: boolean;
            includePaths?: string[];
            onlyActive?: boolean;
            flow?: ValidationContext['flow'];
        }): boolean => {
            if (options?.flow) {
                // Rationale:
                // - When a flow override is provided, we must evaluate rule applicability under that flow WITHOUT
                //   mutating engine.ctx.flow (and without overwriting the primary errorStore cache).
                // - We implement this as a direct per-path evaluation loop, which can early-exit on the first invalid
                //   result and avoids building the nested error-state structure.
                //
                // Consistency note:
                // - The no-flow branch currently delegates to getFormErrorState(), which may refresh via validateAll()
                //   and warms the main cache. Changing that to an early-exit loop could alter those side effects.
                //
                // Consistency options (defer until behavior is fully validated):
                // 1) Make BOTH branches delegate to getFormErrorState() (most consistent, simplest).
                // 2) Make BOTH branches use an early-exit evaluator (more performant, but changes cache-warming semantics).
                const evalPaths = options?.includePaths?.length
                    ? expandIncludePathsToLeafPaths(options.includePaths)
                    : expandEvaluationPaths(Array.from(ruleStore.keys()));
                for (const p of evalPaths) {
                    const res = evaluatePathForFlow(p, options.flow);
                    if (!res.valid) return true;
                }
                return false;
            }

            const formErrors = engine.getFormErrorState(options);
            return deepHasError(formErrors);
        },
        commit: (pathsOverride?: string[], commitOptions?: { validate?: boolean }) => {
            const commitConfig = options?.commitConfig;
            if (!commitConfig || typeof commitConfig.onCommit !== 'function') {
                return;
            }

            const validate = commitOptions?.validate ?? true;

            const paths = pathsOverride?.length
                ? pathsOverride
                : dispatchMode === 'onCommit'
                ? expandEvaluationPaths(Array.from(ruleStore.keys()))
                : [];
            if (!paths.length) {
                return;
            }

            const keyStrategy = commitConfig.keyStrategy ?? 'leaf';
            const errorFlags: Record<string, boolean> = {};

            // By default, commit validates each path to ensure flags reflect current `ctx.values`.
            // Consumers may opt out (commitOptions.validate=false) to reuse cached results.
            for (const path of paths) {
                const result = validate ? engine.validatePath(path) : errorStore.get(path) ?? evaluatePath(path);

                const normalized = path.replace(/\[(\d+)\]/g, '.$1');
                const derivedKey =
                    keyStrategy === 'path' ? normalized : normalized.split('.').filter(Boolean).slice(-1)[0] ?? normalized;

                const key = commitConfig.keyMap?.[path] ?? derivedKey;
                errorFlags[key] = !result.valid;
            }

            commitConfig.onCommit(errorFlags);
        }
    };

    // Auto-register initial config if provided
    if (options?.initialConfig) {
        engine.registerConfig(options.initialConfig);
    }

    return engine;
}
